/**
 * Centralized permission checks for role-based access control
 *
 * Roles:
 * - system-admin: Platform owner (hidden) - manages subscriptions, features, troubleshooting
 * - owner: Organization owner - controls settings, manages users
 * - admin: Full operational access within organization
 * - employee: View site activity, check-ins, and who is on site
 */

// Role constants to avoid string typos
export const ROLES = {
  SYSTEM_ADMIN: 'system-admin',
  OWNER: 'owner',
  ADMIN: 'admin',
  EMPLOYEE: 'employee',
};

// Admin-level roles (full system access)
const ADMIN_ROLES = [ROLES.SYSTEM_ADMIN, ROLES.OWNER, ROLES.ADMIN];

// Management roles (same as admin roles – no intermediate tier)
const MANAGEMENT_ROLES = [ROLES.SYSTEM_ADMIN, ROLES.OWNER, ROLES.ADMIN];

// All known roles
const ALL_ROLES = [ROLES.SYSTEM_ADMIN, ROLES.OWNER, ROLES.ADMIN, ROLES.EMPLOYEE];

/**
 * Check if user is platform owner
 */
export const isOwner = (user) => {
  if (!user) return false;
  return user.is_platform_owner === true || user.isPlatformOwner === true;
};

/**
 * Check if user has admin-level access
 */
export const isAdmin = (role) => ADMIN_ROLES.includes(role);

/**
 * Check if user has management-level access
 */
export const isManagement = (role) => MANAGEMENT_ROLES.includes(role);

// ============================================
// Equipment Permissions
// ============================================

/**
 * Can add new equipment
 */
export const canAddEquipment = (role) => MANAGEMENT_ROLES.includes(role);

/**
 * Can edit equipment details
 */
export const canEditEquipment = (role) => MANAGEMENT_ROLES.includes(role);

/**
 * Can delete equipment
 */
export const canDeleteEquipment = (role) => ADMIN_ROLES.includes(role);

/**
 * Can view deleted equipment list
 */
export const canViewDeletedEquipment = (role) => ADMIN_ROLES.includes(role);

/**
 * Can restore deleted equipment
 */
export const canRestoreEquipment = (role) => ADMIN_ROLES.includes(role);

// ============================================
// Test Permissions
// ============================================

/**
 * Can assign tests to users
 */
export const canAssignTests = (role) => MANAGEMENT_ROLES.includes(role);

/**
 * Can complete tests (all roles can complete their assigned tests)
 */
export const canCompleteTests = (role) => ALL_ROLES.includes(role);

/**
 * Can sign off completed tests
 */
export const canSignOffTests = (role) => MANAGEMENT_ROLES.includes(role);

/**
 * Can approve retest requests
 */
export const canApproveRetests = (role) => MANAGEMENT_ROLES.includes(role);

// ============================================
// Reactive Jobs Permissions
// ============================================

/**
 * Can create reactive jobs
 */
export const canCreateReactiveJobs = (role) => ALL_ROLES.includes(role);

/**
 * Can assign reactive jobs to users
 */
export const canAssignReactiveJobs = (role) => MANAGEMENT_ROLES.includes(role);

/**
 * Can sign off completed reactive jobs
 */
export const canSignOffReactiveJobs = (role) => ADMIN_ROLES.includes(role);

// ============================================
// User Management Permissions
// ============================================

/**
 * Can view user management
 */
export const canManageUsers = (role) => ADMIN_ROLES.includes(role);

/**
 * Can invite new users
 */
export const canInviteUsers = (role) => ADMIN_ROLES.includes(role);

/**
 * Can edit user roles
 */
export const canEditUserRoles = (role) => ADMIN_ROLES.includes(role);

/**
 * Can deactivate users
 */
export const canDeactivateUsers = (role) => ADMIN_ROLES.includes(role);

/**
 * Can resend password reset
 */
export const canResendPassword = (role) => ADMIN_ROLES.includes(role);

// ============================================
// Settings & Configuration Permissions
// ============================================

/**
 * Can manage locations
 */
export const canManageLocations = (role) => ADMIN_ROLES.includes(role);

/**
 * Can manage equipment types
 */
export const canManageEquipmentTypes = (role) => ADMIN_ROLES.includes(role);

/**
 * Can access system settings (sign-off policy, role labels)
 */

export const canAccessSystemSettings = (role) => role === ROLES.ADMIN || role === ROLES.OWNER;

/**
 * Can manage organizations
 */
export const canManageOrganizations = (role) => ADMIN_ROLES.includes(role);

/**
 * Can view audit logs
 */
export const canViewAuditLogs = (role) => ADMIN_ROLES.includes(role);

/**
 * Can generate reports
 */
export const canGenerateReports = (role) => ADMIN_ROLES.includes(role);

/**
 * Can access hours tracking
 */
export const canAccessHoursTracking = (role) => MANAGEMENT_ROLES.includes(role);

// ============================================
// Sidebar Menu Visibility
// ============================================

/**
 * Get visible sidebar items for a role
 * Returns an object with boolean flags for each menu section
 */
export const getSidebarPermissions = (role) => ({
  dashboard: true,
  equipment: true,
  myTasks: true,
  schedule: true,
  reactiveJobs: canCreateReactiveJobs(role),
  myReactiveJobs: false,
  signOffQueue: canSignOffTests(role),
  retestSignOff: canApproveRetests(role),
  failedTests: isManagement(role),
  reports: canGenerateReports(role),
  hoursTracking: canAccessHoursTracking(role),
  userManagement: canManageUsers(role),
  locations: canManageLocations(role),
  equipmentTypes: canManageEquipmentTypes(role),
  organizations: canManageOrganizations(role),
  auditLog: canViewAuditLogs(role),
  deletedEquipment: canViewDeletedEquipment(role),
  accountSecurity: true,
});
